import { getMissingAddressFields } from '@retracedgmbh/modules/companies/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { aboutProfileQueryOptions } from '@/app/(client)/network/-hooks/useGeneralInformation';
import { usePermissions } from '@/hooks/usePermissions';
import {
  isAddressBannerSnoozed,
  snoozeAddressBanner,
  subscribeAddressBannerSnooze,
} from '@/lib/utils/addressBannerState';
import { useSessionStore } from '@/store/sessionStore';

/**
 * Visibility for the "your company address is incomplete" banner.
 *
 * Asks the members who can actually fix it — `network:edit`, which the profile
 * PATCH guards on. Impersonated sessions are asked too: an operator may fill
 * the address in on the client's behalf. The own ABOUT profile is read from
 * the same cache entry as the profile page, so saving the address there hides
 * the banner straight away.
 */
export function useAddressMissingBanner() {
  const user = useSessionStore(s => s.user);
  const userId = user?.id ?? '';
  const companyId = user?.company?.id ?? user?.companyId ?? '';
  const { network } = usePermissions();

  const eligible = Boolean(userId && companyId) && network.canEdit;

  const { data: about } = useQuery({
    ...aboutProfileQueryOptions(companyId),
    enabled: eligible,
  });

  const getSnoozed = useCallback(
    () => isAddressBannerSnoozed(userId, companyId),
    [userId, companyId],
  );
  const snoozed = useSyncExternalStore(
    subscribeAddressBannerSnooze,
    getSnoozed,
    () => true, // first paint: assume snoozed so the banner never flashes
  );

  const missingFields = about ? getMissingAddressFields(about.data) : [];
  const visible = eligible && !snoozed && missingFields.length > 0;

  function handleDismiss() {
    snoozeAddressBanner(userId, companyId);
  }

  return { visible, companyId, missingFields, handleDismiss };
}
